import { BookOpen, TrendingDown } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useModuleFunnel } from '@/services/admin/metrics.service'
import type { DashboardFilters } from './types'

interface Props {
  filters: DashboardFilters
}

/**
 * Funil de módulos — barras horizontais que diminuem mostrando drop-off.
 *
 * Sem courseId: empty state pedindo pra filtrar. Com courseId: mostra
 * cada módulo (largura proporcional ao started) + completed em destaque +
 * dropToNext em vermelho/laranja.
 */
export function ModuleFunnelChart({ filters }: Props) {
  const { data, isLoading } = useModuleFunnel({
    courseId: filters.courseId,
    companyId: filters.companyIds,
  })

  if (!filters.courseId) {
    return (
      <Card className="glass-card border-border/50 h-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingDown className="w-4 h-4 text-primary" />
            Funil de módulos
          </CardTitle>
          <CardDescription>Selecione um curso pra ver o funil</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-12 text-sm text-muted-foreground">
          <BookOpen className="w-8 h-8 opacity-30" />
        </CardContent>
      </Card>
    )
  }

  if (isLoading) {
    return (
      <Card className="glass-card border-border/50 h-full">
        <CardHeader>
          <Skeleton className="h-5 w-40" />
        </CardHeader>
        <CardContent className="space-y-3">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </CardContent>
      </Card>
    )
  }

  if (!data || data.steps.length === 0) {
    return (
      <Card className="glass-card border-border/50 h-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingDown className="w-4 h-4 text-primary" />
            Funil de módulos
          </CardTitle>
        </CardHeader>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Sem módulos publicados nesse curso.
        </CardContent>
      </Card>
    )
  }

  // Maior `started` define largura 100% (proporção entre módulos)
  const maxStarted = Math.max(...data.steps.map((s) => s.started), 1)

  return (
    <Card className="glass-card border-border/50 h-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <TrendingDown className="w-4 h-4 text-primary" />
          Funil de módulos
        </CardTitle>
        <CardDescription className="line-clamp-1">{data.courseName}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Legenda — cor → significado (feedback do user 2026-05-15) */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[10px] text-muted-foreground pb-2 border-b border-border/40">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-sky-500/30" />
            Iniciaram o módulo
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-gradient-to-r from-emerald-500/70 to-emerald-500/60" />
            Concluíram todos os episódios
          </span>
          <span className="ml-auto opacity-70">x/y = concluíram/iniciaram</span>
        </div>
        {data.steps.map((step, idx) => {
          const widthPct = (step.started / maxStarted) * 100
          const completedPct = step.started > 0 ? (step.completed / step.started) * 100 : 0
          return (
            <div key={step.moduleId} className="space-y-1.5">
              <div className="flex items-baseline justify-between text-xs">
                <span className="font-medium truncate flex-1 mr-2">
                  {idx + 1}. {step.moduleName}
                </span>
                <span className="text-muted-foreground tabular-nums whitespace-nowrap">
                  {step.completed}/{step.started}
                </span>
              </div>
              {/* Track + barras sobrepostas */}
              <div className="relative h-7 bg-muted/30 rounded-md overflow-hidden">
                {/* Barra "started" (fundo) */}
                <div
                  className="absolute inset-y-0 left-0 bg-sky-500/30 transition-all duration-500"
                  style={{ width: `${widthPct}%` }}
                />
                {/* Barra "completed" (sobreposta, mais escura) */}
                <div
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-500/70 to-emerald-500/60 transition-all duration-700"
                  style={{ width: `${widthPct * (completedPct / 100)}%` }}
                />
                <div className="absolute inset-0 flex items-center justify-end pr-2 text-[10px] font-semibold tabular-nums">
                  {completedPct.toFixed(0)}%
                </div>
              </div>
              {step.dropToNext > 0 && idx < data.steps.length - 1 && (
                <p className="text-[10px] text-amber-500/80 flex items-center gap-1">
                  <TrendingDown className="w-3 h-3" />
                  {step.dropToNext} usuários pararam aqui
                </p>
              )}
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}
